import {request} from './apiClient';
import {patientService} from './patientService';
import {
  isRingCentralVideoCallLink,
  isSupportedVideoCallLink,
  normalizeVideoCallLink,
} from '../common/videoCallLinks';

// Error class for developer API operations
class DeveloperError extends Error {
  type: string;
  originalError: any;

  constructor(type: string, message: string, originalError: any = null) {
    super(message);
    this.name = 'DeveloperError';
    this.type = type;
    this.originalError = originalError;
  }

  static invalidRequest = new DeveloperError(
    'invalidRequest',
    'Something went wrong. Please try again.',
  );
  static invalidResponse = new DeveloperError(
    'invalidResponse',
    'Something went wrong. Please try again.',
  );
  static networkError = (error: any) =>
    new DeveloperError(
      'networkError',
      'Network error. Please check your connection and try again.',
      error,
    );
  static decodingError = (error: any) =>
    new DeveloperError(
      'decodingError',
      'Something went wrong. Please try again.',
      error,
    );
  static serverError = (statusCode: number, responseData: any) =>
    new DeveloperError(
      'serverError',
      'Something went wrong. Please try again.',
      {statusCode, responseData},
    );
}

/**
 * Create a new API key for the developer
 * @param apiKeyData - The API key configuration
 * @param apiKeyData.name - Name for the API key
 * @param apiKeyData.expiresAt - Expiration date in ISO format
 * @param [apiKeyData.companyId] - Optional company ID
 * @returns The created API key data
 */
export const createApiKey = async (apiKeyData: {
  name: string;
  expiresAt?: string;
  companyId?: string | null;
}): Promise<any> => {
  try {
    // Validate required fields
    if (!apiKeyData.name) {
      throw new DeveloperError('invalidRequest', 'Name is required');
    }

    // Prepare payload data
    const payload = {
      name: apiKeyData.name,
      expiresAt:
        apiKeyData.expiresAt ||
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // Default to 1 year
      companyId: apiKeyData.companyId || null,
    };

    // Make the API call (token attached + base URL resolved by apiClient)
    const response = await request('/developer/api-keys', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(payload),
    });

    // Check status code
    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    // Parse and return the response
    const responseData = await response.json();

    // Validate response structure
    if (!responseData.success || !responseData.data.clientKey) {
      if (responseData.error) {
        console.error('ERROR: Failed to create API key:', responseData.error);
        throw new DeveloperError(
          'serverError',
          'Something went wrong. Please try again.',
          {responseError: responseData.error},
        );
      }
      throw new DeveloperError(
        'invalidResponse',
        'Something went wrong. Please try again.',
      );
    }

    return responseData;
  } catch (error) {
    // Handle specific error types
    if (error instanceof DeveloperError) {
      throw error;
    }
    console.error('ERROR: Network error occurred:', error);
    throw DeveloperError.networkError(error);
  }
};

/**
 * Get all API keys for the current user
 * @returns Array of API key objects
 */
export const getApiKeys = async (): Promise<any[]> => {
  try {
    // Make the API call
    const response = await request('/developer/api-keys', {
      method: 'GET',
      parse: 'raw',
    });

    // Check status code
    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    // Parse and return the response
    const responseData = await response.json();
    return responseData.apiKeys || responseData || [];
  } catch (error) {
    // Handle specific error types
    if (error instanceof DeveloperError) {
      throw error;
    }
    throw DeveloperError.networkError(error);
  }
};

/**
 * Revoke/delete an API key
 * @param apiKeyId - The ID of the API key to revoke
 * @returns Success response
 */
export const revokeApiKey = async (apiKeyId: string): Promise<any> => {
  if (!apiKeyId) {
    throw DeveloperError.invalidRequest;
  }

  try {
    // Make the API call
    const response = await request(`/developer/api-keys/${apiKeyId}`, {
      method: 'DELETE',
      parse: 'raw',
    });

    // Check status code
    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    // Parse and return the response
    const responseData = await response.json();
    return responseData;
  } catch (error) {
    // Handle specific error types
    if (error instanceof DeveloperError) {
      throw error;
    }
    throw DeveloperError.networkError(error);
  }
};

const BOT_API_PATH = '/api/v1/bot';

/**
 * Create a new bot
 * @param botData - The bot configuration
 * @param botData.language - The language for the bot
 * @param botData.meetingURL - The meeting URL
 * @param [botData.dialect] - Optional dialect for the language
 * @param [botData.simplifyMedicalTerminology] - Enable simplified medical terms
 * @param [botData.patientId] - Optional linked local patient ID
 * @param [botData.ehrPatientId] - Optional linked EHR patient ID
 * @param [botData.patientName] - Optional linked patient display name
 * @param [botData.joinAt] - Optional ISO-8601 string or Date.
 *   For Zoom, Meet, Teams, WebEx, and Slack, a future joinAt schedules the
 *   Recall-backed bot. RingCentral bots are join-now only today; omit joinAt.
 * @returns The created bot data
 */
export interface CreateBotArgs {
  mode?: 'encounter' | 'interpreter' | 'scribe';
  language?: string;
  meetingURL?: string;
  dialect?: string;
  simplifyMedicalTerminology?: boolean;
  patientId?: string;
  ehrPatientId?: string;
  patientName?: string;
  joinAt?: string | Date | null;
}

export const createBot = async (botData: CreateBotArgs): Promise<any> => {
  try {
    const meetingURL = normalizeVideoCallLink(botData.meetingURL);
    if (!isSupportedVideoCallLink(meetingURL)) {
      throw new DeveloperError(
        'invalidRequest',
        'Enter a supported video call link.',
      );
    }

    const joinAtIso = normalizeJoinAt(botData.joinAt);
    if (joinAtIso && isRingCentralVideoCallLink(meetingURL)) {
      throw new DeveloperError(
        'invalidRequest',
        'RingCentral video links cannot be scheduled.',
      );
    }

    // Unified summon endpoint: the product is the route, deviceKind: 'video'
    // selects the meeting-bot modality. Scribe mode hits /v1/scribe/sessions;
    // everything else is the live interpreter encounter.
    const endpoint =
      botData.mode === 'scribe'
        ? '/v1/scribe/sessions'
        : '/v1/interpreter/sessions';
    const response = await request(endpoint, {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify({
        deviceKind: 'video',
        ...(botData.language && {language: botData.language}),
        meetingURL,
        ...(botData.dialect && {dialect: botData.dialect}),
        ...(botData.simplifyMedicalTerminology && {
          simplifyMedicalTerminology: true,
        }),
        ...(joinAtIso && {joinAt: joinAtIso}),
        context: {
          ...(botData.patientId && {patientId: botData.patientId}),
          ...(botData.ehrPatientId && {ehrPatientId: botData.ehrPatientId}),
          ...(botData.patientName && {patientName: botData.patientName}),
        },
      }),
    });

    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof DeveloperError) {
      throw error;
    }
    throw DeveloperError.networkError(error);
  }
};

/**
 * Reschedule an already-scheduled bot's join time.
 *
 * Recall.ai disallows updates within 10 minutes of the current `join_at`.
 * If the backend returns a 400 with a reschedule failure, surface the
 * message so the UI can offer "cancel and recreate" as a fallback.
 *
 * @param botId - Recall bot ID.
 * @param joinAt - ISO-8601 string or Date > 10 min from now.
 */
export const updateScheduledBot = async (
  botId: string,
  joinAt: string | Date,
): Promise<any> => {
  if (!botId) throw DeveloperError.invalidRequest;
  const joinAtIso = normalizeJoinAt(joinAt);
  if (!joinAtIso) {
    throw new DeveloperError('invalidRequest', 'joinAt is required');
  }

  try {
    const response = await request(
      `${BOT_API_PATH}/${encodeURIComponent(botId)}/schedule`,
      {
        method: 'PATCH',
        parse: 'raw',
        body: JSON.stringify({joinAt: joinAtIso}),
      },
    );

    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof DeveloperError) throw error;
    throw DeveloperError.networkError(error);
  }
};

/**
 * Cancel a scheduled bot (or, if already dispatched, make it leave the call).
 * @param botId
 * @param [reason] - Optional free-form cancellation reason, stored on the RecallBot doc.
 */
export const cancelScheduledBot = async (
  botId: string,
  reason?: string,
): Promise<any> => {
  if (!botId) throw DeveloperError.invalidRequest;

  try {
    const response = await request(
      `${BOT_API_PATH}/${encodeURIComponent(botId)}/schedule`,
      {
        method: 'DELETE',
        parse: 'raw',
        body: JSON.stringify(reason ? {reason} : {}),
      },
    );

    if (!response.ok) {
      const responseData = await response.text();
      throw DeveloperError.serverError(response.status, responseData);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof DeveloperError) throw error;
    throw DeveloperError.networkError(error);
  }
};

const normalizeScheduledBotPatientPayload = ({
  patientId = null,
  ehrPatientId = null,
  patientName = '',
} = {}) => ({
  patientId: patientId || null,
  ehrPatientId: ehrPatientId || null,
  patientName: patientName || '',
});

const withScheduledBotPatientFieldNames = (payload: any, updated: any) => ({
  ...(updated || {}),
  patientId: payload.patientId,
  ehrPatientId: payload.ehrPatientId,
  patientName: payload.patientName,
  patient_id: payload.patientId,
  ehr_patient_id: payload.ehrPatientId,
  patient_name: payload.patientName,
});

// Patient reassignment persists through the session-patient route, so it
// needs a conversation-backed bot (`telecall_id`). Scheduled-only bots have
// no reassignment route on the backend (the old POST /api/v1/bot/:botId/patient
// was removed) — callers hide the affordance for them, and this throws as a
// backstop rather than faking success.
export const canReassignScheduledBotPatient = (bot: any): boolean =>
  Boolean(bot?.conversation && bot?.telecall_id);

export const reassignScheduledBotPatient = async (
  bot: any,
  patientPayload: any,
): Promise<any> => {
  if (!bot?.id) return null;
  if (!canReassignScheduledBotPatient(bot)) {
    throw new DeveloperError(
      'invalidRequest',
      'This scheduled session cannot change patients yet.',
    );
  }
  const payload = normalizeScheduledBotPatientPayload(patientPayload);
  const updated = await patientService.reassignSessionPatient(
    bot.telecall_id,
    payload,
  );

  return withScheduledBotPatientFieldNames(payload, updated);
};

// Coerces Date | string | null/undefined -> ISO-8601 string | null.
function normalizeJoinAt(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export {DeveloperError};
